import { mergeStyleSets, useTheme } from '@fluentui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CopilotAgentEvent, CopilotAgentEventKind } from 'services/SignalR/CopilotHub';
import { CopilotActivityItem, CopilotActivityItemState, CopilotActivityTicker } from './CopilotActivityTicker';

interface ICopilotAgentEventListProps {
  events: CopilotAgentEvent[];
}

const progressVariantCount = 3;

// The tool name of the event the API sends for code the model ran in its provider's sandbox; its payload carries a
// preview of the output (stdout, at most 2,000 characters) and whether the run failed.
const codeExecutionToolName = 'code_execution';
const codeExecutionPreviewLength = 80;
const traceProgressVariantCount = 9;

// One ticker row: a tool call with its result merged in, a Trace step updated in place, or a single event
interface IActivityRow {
  key: string;
  event: CopilotAgentEvent;
  // the tool call's result, once it arrived
  result?: CopilotAgentEvent;
  text?: string;
  isCall: boolean;
}

export const CopilotAgentEventList = (props: ICopilotAgentEventListProps): JSX.Element | null => {
  const { t } = useTranslation(['copilot']);
  const theme = useTheme();

  const visibleEvents = useMemo(
    () =>
      props.events.filter(
        (event) =>
          event.kind === CopilotAgentEventKind.ToolCall ||
          event.kind === CopilotAgentEventKind.ToolResult ||
          event.kind === CopilotAgentEventKind.Trace ||
          event.kind === CopilotAgentEventKind.Error,
      ),
    [props.events],
  );

  // A tool call is one row: its result marks it done and brings the finished line. A Trace event with a text
  // replaces the row of the same traceName, so a counting step stays one row.
  const rows = useMemo(() => {
    const result: IActivityRow[] = [];
    const callRows = new Map<string, IActivityRow>();
    const traceRows = new Map<string, IActivityRow>();
    visibleEvents.forEach((event, index) => {
      const eventOrder = event.sequence ?? event.clientSequence ?? index;
      if (event.kind === CopilotAgentEventKind.ToolCall) {
        const row: IActivityRow = {
          key: `call-${event.callId ?? eventOrder}`,
          event,
          text: event.text,
          isCall: true,
        };
        if (event.callId) callRows.set(event.callId, row);
        result.push(row);

        return;
      }

      const call =
        event.kind === CopilotAgentEventKind.ToolResult && event.callId ? callRows.get(event.callId) : undefined;
      if (call) {
        call.result = event;
        call.text = event.text || call.text;

        return;
      }

      if (event.kind === CopilotAgentEventKind.Trace && event.text && event.traceName) {
        const trace = traceRows.get(event.traceName);
        if (trace) {
          trace.text = event.text;

          return;
        }

        const row: IActivityRow = {
          key: `trace-${event.traceName}-${eventOrder}`,
          event,
          text: event.text,
          isCall: false,
        };
        traceRows.set(event.traceName, row);
        result.push(row);

        return;
      }

      result.push({ key: `${event.kind}-${eventOrder}`, event, text: event.text, isCall: false });
    });

    return result;
  }, [visibleEvents]);

  // An event without user-facing text gets a rotating set of localized verbs (indexed by the row's position)
  // instead of the raw payload.
  const getActivityText = (event: CopilotAgentEvent, variant: number): string => {
    switch (event.kind) {
      case CopilotAgentEventKind.ToolCall:
      case CopilotAgentEventKind.ToolResult:
        if (event.toolName === codeExecutionToolName) return getCodeExecutionText(event);

        switch (variant) {
          case 0:
            return t('copilot:AgentEvents.Progress.Reviewing');
          case 1:
            return t('copilot:AgentEvents.Progress.Checking');
          default:
            return t('copilot:AgentEvents.Progress.Summarizing');
        }
      case CopilotAgentEventKind.Trace:
        switch (variant) {
          case 0:
            return t('copilot:AgentEvents.Progress.Working');
          case 1:
            return t('copilot:AgentEvents.Progress.Planning');
          case 2:
            return t('copilot:AgentEvents.Progress.Preparing');
          case 3:
            return t('copilot:AgentEvents.Progress.Connecting');
          case 4:
            return t('copilot:AgentEvents.Progress.Refining');
          case 5:
            return t('copilot:AgentEvents.Progress.LookingUp');
          case 6:
            return t('copilot:AgentEvents.Progress.Searching');
          case 7:
            return t('copilot:AgentEvents.Progress.Gathering');
          default:
            return t('copilot:AgentEvents.Progress.GettingCloser');
        }
      case CopilotAgentEventKind.Error:
        return t('copilot:AgentEvents.Progress.Error');
      default:
        return '';
    }
  };

  // The ran-code line: the first line of the output's preview, cut to fit the row, or that the code failed.
  const getCodeExecutionText = (event: CopilotAgentEvent): string => {
    const payload = (event.payload ?? {}) as { stdout?: unknown; failed?: unknown };
    if (payload.failed === true) return t('copilot:AgentEvents.CodeExecutionFailed');

    const firstLine =
      typeof payload.stdout === 'string' ? payload.stdout.split('\n').find((line) => line.trim() !== '') ?? '' : '';
    if (!firstLine) return t('copilot:AgentEvents.CodeExecuted');

    const output =
      firstLine.length > codeExecutionPreviewLength
        ? `${firstLine.slice(0, codeExecutionPreviewLength)}...`
        : firstLine;

    return t('copilot:AgentEvents.CodeExecutedWithOutput', { output });
  };

  // Only the last row spins, also while parallel calls are still running; the rows before it are done
  const getState = (row: IActivityRow, isLast: boolean): CopilotActivityItemState => {
    if (row.event.kind === CopilotAgentEventKind.Error) {
      return 'error';
    }

    return isLast ? 'active' : 'done';
  };

  // The server's words when the event carries them; else the ran-code line or today's generic verb
  const getLabel = (row: IActivityRow, index: number): string => {
    if (row.text) return row.text;

    const event = row.result ?? row.event;
    const variantCount = event.kind === CopilotAgentEventKind.Trace ? traceProgressVariantCount : progressVariantCount;

    return getActivityText(event, index % variantCount);
  };

  const items: CopilotActivityItem[] = rows.map((row, index) => ({
    key: row.key,
    label: getLabel(row, index),
    state: getState(row, index === rows.length - 1),
  }));

  const styles = mergeStyleSets({
    root: {
      paddingLeft: 48,
      paddingRight: 10,
      overflow: 'hidden',
    },
    container: {
      alignItems: 'flex-start',
      background: theme.semanticColors.bodyBackground,
      border: `1px solid ${theme.semanticColors.bodyDivider}`,
      borderRadius: 14,
      boxShadow: theme.effects.elevation4,
      color: theme.semanticColors.bodySubtext,
      display: 'block',
      maxWidth: 'calc(100% - 50px)',
      padding: '6px 10px',
      width: 'fit-content',
    },
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <CopilotActivityTicker items={items} />
      </div>
    </div>
  );
};
